import { useState, useEffect, useCallback } from "react";
import { api, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLocale } from "@/i18n/LocaleContext";

export default function SafetyCheckinBanner() {
  const { user } = useAuth();
  const { t } = useLocale();
  const [checkin, setCheckin] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/safety-checkins/pending");
      setCheckin((r.data.checkins || [])[0] || null);
    } catch {
      // silent
    }
  }, [user]);

  useEffect(() => {
    fetchPending();
    const iv = setInterval(fetchPending, 60000);
    return () => clearInterval(iv);
  }, [fetchPending]);

  const confirm = async () => {
    if (!checkin || confirming) return;
    setConfirming(true);
    try {
      await api.post(`/safety-checkins/${checkin.checkin_id}/confirm`);
      toast.success(t("checkin.confirmed"));
      setCheckin(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm");
    } finally {
      setConfirming(false);
    }
  };

  if (!user || !checkin) return null;
  return (
    <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md" data-testid="safety-checkin-banner">
      <div className="glass-card border border-emerald-500/40 bg-emerald-950/70 backdrop-blur-xl p-4 shadow-2xl rounded-2xl">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{t("checkin.title")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("checkin.body").replace("{email}", checkin.friend_email || "")}</p>
          </div>
        </div>
        <Button
          className="w-full mt-3 bg-emerald-600 hover:bg-emerald-500 text-white"
          onClick={confirm}
          disabled={confirming}
          data-testid="checkin-confirm-btn"
        >
          {t("checkin.confirmBtn")}
        </Button>
      </div>
    </div>
  );
}
